import { Alert, Button, Col, Icon, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useRef, useState } from "react";
import { say } from "../localization/say.ts";
import { passkeysWork, signInWithAPasskey } from "../net/rally.ts";
import { why } from "../net/why.ts";
import { Doorway } from "./doorway.tsx";

export function SignIn({ onIn }: { onIn: () => void }) {
  const theme = useTheme();
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const quiet = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!passkeysWork()) return;
    const stop = new AbortController();
    quiet.current = stop;
    signInWithAPasskey("conditional", stop.signal)
      .then(onIn)
      .catch(() => {});
    return () => stop.abort();
  }, [onIn]);

  async function signIn() {
    quiet.current?.abort();
    setBusy(true);
    setRefused(null);
    try {
      await signInWithAPasskey("optional");
      onIn();
    } catch (cause) {
      setRefused(why(cause, "signIn"));
    } finally {
      setBusy(false);
    }
  }

  if (!passkeysWork()) {
    return (
      <Doorway title={say("door.signIn")}>
        <Text typeName="body" color="text.secondary">
          {say("signIn.noPasskeys")}
        </Text>
      </Doorway>
    );
  }

  return (
    <Doorway title={say("door.signIn")}>
      {refused ? <Alert tone="danger">{refused}</Alert> : null}
      <Button variant="primary" block loading={busy} onPress={signIn} testID="sign-in">
        {say("signIn.press")}
      </Button>
      <Col style={{ flexDirection: "row", gap: theme.space[2], alignItems: "center" }}>
        <Icon name="shield" size={16} color="text.secondary" />
        <Text typeName="caption" color="text.secondary" style={{ flex: 1 }}>
          {say("signIn.how")}
        </Text>
      </Col>
    </Doorway>
  );
}
